import { useState, useContext, useEffect, useRef } from "react";
import { Link } from 'react-router-dom';
import config from "../../config";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./Main.css";
import logo from "../../assets/images/RedP_LogoT.png";
import { AuthContext } from "../../context/AuthContext";
import Loader from "../../components/loader/loader";
import ContactForm from "../../components/contactform/ContactForm";
import Customizations from "../customization/Home/CustomizationHome"; 

const PDF_TABLE_OPTIONS = {
  theme: "plain",
  showHead: "never",
  styles: {
    cellPadding: 2,
    fontSize: 9,
    textColor: [65, 65, 65],
    valign: "middle",
  },
  alternateRowStyles: { fillColor: [245, 246, 248] },
  columnStyles: {
    0: { cellWidth: 72 },
    1: { cellWidth: 110 },
  },
  margin: { left: 14, right: 14 },
};

const createPdfDocument = (caseAlias) => {
  const doc = new jsPDF();
  const title = String(caseAlias || "").trim() || "Contract Summary";
  doc.setFontSize(18);
  const titleLines = doc.splitTextToSize(title, 182);
  doc.text(titleLines, 14, 20);
  return { doc, startY: 23 + (titleLines.length * 7) };
};

const getExportBaseName = (caseAlias, customizationName, fallback = "Download") => {
  const name = [caseAlias, customizationName]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ") || fallback;
  const printableName = Array.from(name).filter((character) => character.charCodeAt(0) >= 32).join("");
  return printableName.replace(/[<>:"/\\|?*]/g, "-").replace(/\s+/g, " ").trim();
};

const EXPORT_FORMAT_OPTIONS = [
  { value: "pdf", label: "PDF", description: "Printable summary" },
  { value: "xlsx", label: "Excel", description: "Spreadsheet workbook" },
  { value: "json", label: "JSON", description: "Structured data" },
  { value: "csv", label: "CSV", description: "Comma-separated values" },
  { value: "xml", label: "XML", description: "XML data file" },
  { value: "txt", label: "TXT", description: "Plain-text summary" },
  { value: "docx", label: "DOCX", description: "Word document" },
  { value: "ics", label: "ICS", description: "Calendar dates" },
];

const APPFILES_JOB_POLL_MS = 500;
const APPFILES_CONFLICT_CHECK_MESSAGE = "Joining the AppFiles queue...";
const wait = (milliseconds) => new Promise(resolve => setTimeout(resolve, milliseconds));

// --- ICONS ---
const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
    <line x1="1" y1="1" x2="23" y2="23"></line>
  </svg>
);

// Logout Icon
const LogoutIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
    <polyline points="16 17 21 12 16 7"></polyline>
    <line x1="21" y1="12" x2="9" y2="12"></line>
  </svg>
);

const DocumentIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <line x1="10" y1="9" x2="8" y2="9"></line>
  </svg>
);

const ArrowUpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="19" x2="12" y2="5"></line>
    <polyline points="5 12 12 5 19 12"></polyline>
  </svg>
);

const ChevronIcon = ({ direction }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points={direction === "left" ? "15 18 9 12 15 6" : "9 18 15 12 9 6"}></polyline>
  </svg>
);

const SwapIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 7h16M20 7l-4-4M20 7l-4 4" />
    <path d="M20 17H4M4 17l4-4M4 17l4 4" />
  </svg>
);

const SplitIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
    <rect x="8" y="8" width="8" height="8" rx="2" ry="2"></rect>
  </svg>
);

// Help / Question Mark Icon
const HelpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);

// Calendar Plus Icon for Google Calendar Add
const CalendarPlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
    <line x1="12" y1="14" x2="12" y2="18"></line>
    <line x1="10" y1="16" x2="14" y2="16"></line>
  </svg>
);

const DownloadCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9"></circle>
    <path d="M12 7v8"></path>
    <path d="m8.5 11.5 3.5 3.5 3.5-3.5"></path>
  </svg>
);

const Main = () => {
  const { user, logout } = useContext(AuthContext);
  const token = localStorage.getItem("access_token");
  
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [data, setData] = useState({ contract: null, dueDates: null, fileId: null, customization: "", fileName: "", dateFieldTargets: {} });

  // --- Card Collapse State ---
  const [integrationsCollapsed, setIntegrationsCollapsed] = useState(true);

  // --- Calendar Selection State ---
  const [calendarSelection, setCalendarSelection] = useState(new Set());
  const [exportEmail, setExportEmail] = useState(user?.email || "");
  const [selectedExportFormats, setSelectedExportFormats] = useState(() => new Set(["pdf"]));
  const [sendingExportEmail, setSendingExportEmail] = useState(false);
  const [downloadingExports, setDownloadingExports] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const [formState, setFormState] = useState(() => {
    const saved = sessionStorage.getItem("redp_formState");
    const savedState = saved ? JSON.parse(saved) : {};
    return { caseAlias: "", effectiveDate: "", ...savedState };
  });
  
  const [providerState, setProviderState] = useState({
    providerId: "",
    appFileId: "",
    email: "",
    password: "",
    submitDocuments: true,
    submitBlankFields: true
  });
  
  const [uiState, setUiState] = useState({ 
    loading: false, error: "", fileError: "", providerErrors: {}, loaderText: "Processing...", 
    showPassword: false, dataSent: null, saveStatus: "", isDirty: false,
    sendingToProvider: false, providerStatus: "",
    integrationProgress: null, integrationStage: "",
    integrationQueuePosition: null, integrationCancelable: false,
    integrationCanceling: false
  });
  
  const [acknowledgement, setAcknowledgement] = useState(() => {
    return sessionStorage.getItem("redp_acknowledgement") === "true";
  });

  const [showCustomization, setShowCustomization] = useState(false);
  const [providers, setProviders] = useState([]);

  // --- Help Menu State ---
  const [helpMenuOpen, setHelpMenuOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  // --- Scroll & Preview State ---
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [docViewerState, setDocViewerState] = useState({
    isOpen: false,
    activeFile: null,
    swapLayout: false,
    showMenu: false
  });
  const [previewUrl, setPreviewUrl] = useState(null);

  // --- Field/Section Management Modal State ---
  const [editModal, setEditModal] = useState({
    isOpen: false,
    target: 'field',
    type: null,
    section: null,
    step: 'select',
    selectedItem: null,
    multiSelect: new Set()
  });
  const [newItemName, setNewItemName] = useState("");
  const [effectiveDateEditStart, setEffectiveDateEditStart] = useState(null);
  const [recalculateModal, setRecalculateModal] = useState({
    isOpen: false,
    effectiveDate: "",
    changes: [],
    selected: new Set(),
  });
  const [appFilesConflictModal, setAppFilesConflictModal] = useState({
    isOpen: false,
    conflicts: [],
    selected: new Set(),
    payload: null,
  });
  
  const loaderRef = useRef(null);
  const printRef = useRef();
  const resultsRef = useRef(null);
  const appFilesJobRef = useRef(null);

  useEffect(() => {
    fetch(`${config.REACT_APP_API_URL}/providers`, { headers: { Authorization: token } })
      .then(res => res.json())
      .then(setProviders)
      .catch(e => setUiState(p => ({ ...p, error: `Error: ${e.message}` })));
    
    if (user?.email) {
      setProviderState(prev => ({ ...prev, email: user.email }));
      setExportEmail(user.email);
    }
  }, [token, user]);

  useEffect(() => {
    sessionStorage.setItem("redp_formState", JSON.stringify(formState));
  }, [formState]);

  useEffect(() => {
    sessionStorage.setItem("redp_acknowledgement", acknowledgement);
  }, [acknowledgement]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (docViewerState.activeFile) {
      const url = URL.createObjectURL(docViewerState.activeFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [docViewerState.activeFile]);

  useEffect(() => {
    if (data.contract || data.dueDates) {
      setTimeout(() => {
        const textareas = document.querySelectorAll(".data-table-input");
        textareas.forEach(el => {
          el.style.height = "auto"; 
          el.style.height = el.scrollHeight + "px";
        });
      }, 50);
      
      // Initialize Calendar Selection (default to all dates)
      const { dates: allDates } = getMergedDates();
      if (Object.keys(allDates).length > 0) {
        setCalendarSelection(new Set(Object.keys(allDates)));
      }
    }
  }, [data]);

  // Click outside listener for Help Menu
  useEffect(() => {
    const handleClickOutside = (event) => {
        if (helpMenuOpen && !event.target.closest('.help-widget-container')) {
            setHelpMenuOpen(false);
        }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [helpMenuOpen]);

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      const pdfFiles = droppedFiles.filter(file => file.type === "application/pdf");
      if (pdfFiles.length > 0) {
        setSelectedFiles(prev => [...prev, ...pdfFiles]);
        setUiState(p => ({ ...p, fileError: "" }));
      } else {
        setUiState(p => ({ ...p, fileError: "Please drop PDF files only." }));
      }
    }
  };

  const updateForm = (field, value) => setFormState(prev => ({ ...prev, [field]: value }));
  const updateProviderForm = (field, value) => {
    setProviderState(prev => ({ ...prev, [field]: value }));
    setUiState(prev => ({ ...prev, providerErrors: { ...prev.providerErrors, [field]: "" } }));
  };

  const isGeneratedSplitField = (key) => /\s\(\d+\)$/.test(key);

  const isPartyNameField = (key) => {
    const normalized = String(key || "").toLowerCase().replace(/[_\s]+/g, " ");
    const isPartySide = normalized.includes("buyer") || normalized.includes("seller");
    const isPluralAggregate = normalized === "buyers" || normalized === "sellers";
    return isPluralAggregate || (isPartySide && normalized.includes("name"));
  };

  const getSplitItems = (key, value) => {
    if (!isPartyNameField(key)) return [];
    if (Array.isArray(value)) return value.map(item => String(item).trim()).filter(Boolean);
    if (typeof value !== "string") return [];

    const trimmed = value.trim();
    if (!trimmed) return [];

    if (trimmed.includes("||")) return trimmed.split("||").map(item => item.trim()).filter(Boolean);
    if (trimmed.includes("\n")) return trimmed.split("\n").map(item => item.trim()).filter(Boolean);
    if (trimmed.includes(";")) return trimmed.split(";").map(item => item.trim()).filter(Boolean);
    if (trimmed.includes(",")) return trimmed.split(",").map(item => item.trim()).filter(Boolean);
    if (/\sand\s/i.test(trimmed)) return trimmed.split(/\sand\s/i).map(item => item.trim()).filter(Boolean);

    return [];
  };

  const canSplitField = (key, value) => !isGeneratedSplitField(key) && getSplitItems(key, value).length > 1;

  const handleInputChange = (section, key, value, e) => {
    if (e?.target) { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }
    setData(prev => {
      const newData = { ...prev };
      const isDueDate = section === "dueDates";
      const currentValue = isDueDate ? prev.dueDates[key] : prev.contract[section][key];
      const isList = Array.isArray(currentValue);
      let newValue = value;
      if (isList) newValue = value.split(',').map((s, i) => i === 0 ? s : s.replace(/^\s/, ""));
      if (isDueDate) newData.dueDates = { ...newData.dueDates, [key]: newValue };
      else {
        newData.contract = { ...newData.contract, [section]: { ...newData.contract[section], [key]: newValue } };
        const canonicalDateKey = getCanonicalDateKey(section, key);
        if (canonicalDateKey) {
          newData.dueDates = { ...newData.dueDates, [canonicalDateKey]: newValue };
        }
      }
      return newData;
    });
    setUiState(p => ({ ...p, saveStatus: "", isDirty: true }));
  };

  const normalizeFieldKey = (key) => String(key || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  const getCanonicalDateKey = (section, key) => {
    const stableKey = Object.entries(data.dateFieldTargets || {}).find(([, targets]) =>
      targets.some(target => target.section === section && target.field === key)
    )?.[0];
    if (stableKey) return stableKey;

    const normalizedKey = normalizeFieldKey(key);
    return normalizedKey.endsWith("_date") ? normalizedKey : null;
  };

  const isEffectiveDateField = (section, key) =>
    getCanonicalDateKey(section, key) === "effective_date";

  const handleEffectiveDateFocus = (section, key, value) => {
    if (isEffectiveDateField(section, key)) setEffectiveDateEditStart(String(value || "").trim());
  };

  const handleDateFieldBlur = async (section, key, value) => {
    if (!isEffectiveDateField(section, key)) return;
    const nextEffectiveDate = String(value || "").trim();
    const previousEffectiveDate = effectiveDateEditStart;
    setEffectiveDateEditStart(null);

    if (nextEffectiveDate === previousEffectiveDate) return;
    if (!isDateFormat(nextEffectiveDate)) {
      setUiState(prev => ({
        ...prev,
        saveStatus: "Enter a valid Effective Date in MM/DD/YYYY format.",
        isDirty: true,
      }));
      return;
    }

    const nextContract = {
      ...data.contract,
      [section]: { ...data.contract[section], [key]: nextEffectiveDate },
    };
    const nextDueDates = { ...data.dueDates, effective_date: nextEffectiveDate };

    // Keep the stable due-date value synchronized even if the user elects not
    // to update any dependent deadlines.
    setData(prev => ({ ...prev, contract: nextContract, dueDates: nextDueDates }));
    setUiState(prev => ({ ...prev, saveStatus: "Recalculating dates...", isDirty: true }));

    try {
      const response = await fetch(`${config.REACT_APP_API_URL}/redp/recalculate-dates/preview`, {
        method: "POST",
        headers: { "Authorization": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          effective_date: nextEffectiveDate,
          contract_data: nextContract,
          due_dates: nextDueDates,
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Unable to recalculate dates.");

      setData(prev => ({
        ...prev,
        contract: {
          ...prev.contract,
          [section]: { ...prev.contract[section], [key]: result.effective_date },
        },
        dueDates: { ...prev.dueDates, effective_date: result.effective_date },
      }));

      if (result.changes.length > 0) {
        setRecalculateModal({
          isOpen: true,
          effectiveDate: result.effective_date,
          changes: result.changes,
          selected: new Set(result.changes.map(change => change.key)),
        });
        setUiState(prev => ({ ...prev, saveStatus: "" }));
      } else {
        setUiState(prev => ({ ...prev, saveStatus: "Effective date updated.", isDirty: true }));
      }
    } catch (error) {
      setUiState(prev => ({
        ...prev,
        saveStatus: `Effective date updated; dependent dates could not be recalculated: ${error.message}`,
        isDirty: true,
      }));
    }
  };

  const toggleRecalculatedDate = (key) => {
    setRecalculateModal(prev => {
      const selected = new Set(prev.selected);
      selected.has(key) ? selected.delete(key) : selected.add(key);
      return { ...prev, selected };
    });
  };

  const closeRecalculateModal = () => {
    setRecalculateModal({ isOpen: false, effectiveDate: "", changes: [], selected: new Set() });
    setUiState(prev => ({ ...prev, saveStatus: "Effective date updated; existing deadlines kept.", isDirty: true }));
  };

  const applyRecalculatedDates = () => {
    const selectedChanges = recalculateModal.changes.filter(change =>
      recalculateModal.selected.has(change.key)
    );

    setData(prev => {
      const contract = { ...prev.contract };
      const dueDates = { ...prev.dueDates, effective_date: recalculateModal.effectiveDate };

      selectedChanges.forEach(change => {
        dueDates[change.key] = change.new_date;
        change.targets.forEach(({ section, field }) => {
          contract[section] = { ...contract[section], [field]: change.new_date };
        });
      });

      return { ...prev, contract, dueDates };
    });

    const appliedCount = selectedChanges.length;
    setRecalculateModal({ isOpen: false, effectiveDate: "", changes: [], selected: new Set() });
    setUiState(prev => ({
      ...prev,
      saveStatus: appliedCount > 0
        ? `${appliedCount} dependent date${appliedCount === 1 ? "" : "s"} recalculated.`
        : "Effective date updated; existing deadlines kept.",
      isDirty: true,
    }));
  };

  const handleKeyChange = (section, oldKey, newKey) => {
    const trimmedNewKey = newKey.trim();
    if (!trimmedNewKey || trimmedNewKey === oldKey) return;
    setData(prev => {
      const newData = { ...prev };
      let targetObj = section === "dueDates" ? newData.dueDates : newData.contract[section];
      const newSection = {};
      Object.keys(targetObj).forEach(k => {
        newSection[k === oldKey ? trimmedNewKey : k] = targetObj[k];
      });
      if (section === "dueDates") newData.dueDates = newSection;
      else {
        newData.contract[section] = newSection;
        newData.dateFieldTargets = Object.fromEntries(
          Object.entries(prev.dateFieldTargets || {}).map(([dateKey, targets]) => [
            dateKey,
            targets.map(target =>
              target.section === section && target.field === oldKey
                ? { ...target, field: trimmedNewKey }
                : target
            ),
          ])
        );
      }
      return newData;
    });
    setUiState(p => ({ ...p, saveStatus: "", isDirty: true }));
  };

  const handleSplitField = (section, key) => {
    setData(prev => {
        const newData = { ...prev };
        let targetObj = section === "dueDates" ? prev.dueDates : prev.contract[section];
        const value = targetObj[key];
        const splitItems = getSplitItems(key, value);
        if (splitItems.length < 2) return prev;
        const newSection = {};
        const numberedFieldPattern = new RegExp(`^${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} \\(\\d+\\)$`);
        Object.entries(targetObj).forEach(([k, v]) => {
            if (numberedFieldPattern.test(k)) return;
            newSection[k] = v;
            if (k === key) splitItems.forEach((item, index) => newSection[`${k} (${index + 1})`] = item);
        });
        if (section === "dueDates") newData.dueDates = newSection;
        else newData.contract[section] = newSection;
        return newData;
    });
    setUiState(p => ({ ...p, saveStatus: "", isDirty: true }));
  };

  const handleKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.target.blur(); } };

  // --- EDIT MODAL LOGIC ---
  const openEditModal = (type, section, target = 'field') => {
    setEditModal({ isOpen: true, target, type, section, step: 'select', selectedItem: null, multiSelect: new Set() });
    setNewItemName("");
  };
  const closeEditModal = () => setEditModal(prev => ({ ...prev, isOpen: false }));
  const handleItemSelect = (key) => {
    if (editModal.type === 'remove') {
      setEditModal(prev => {
        const newSet = new Set(prev.multiSelect);
        newSet.has(key) ? newSet.delete(key) : newSet.add(key);
        return { ...prev, multiSelect: newSet };
      });
    } else {
      setEditModal(prev => ({ ...prev, selectedItem: key, step: 'input' }));
    }
  };
  const proceedToDeleteConfirm = () => setEditModal(prev => ({ ...prev, step: 'confirm' }));
  const executeAdd = () => {
    if (!newItemName.trim()) return;
    const safeName = newItemName.trim();
    setData(prev => {
      const newData = { ...prev };
      if (editModal.target === 'section') {
        const newContract = {};
        const contractEntries = Object.entries(prev.contract || {});
        if (editModal.selectedItem === '__TOP__' || contractEntries.length === 0) {
          newContract[safeName] = {};
        }
        contractEntries.forEach(([key, value]) => {
            newContract[key] = value;
            if (key === editModal.selectedItem) newContract[safeName] = {};
        });
        newData.contract = newContract;
      } else {
        const sectionName = editModal.section;
        const newSection = {};
        const sectionEntries = Object.entries(prev.contract[sectionName] || {});
        if (editModal.selectedItem === '__TOP__' || sectionEntries.length === 0) {
          newSection[safeName] = "";
        }
        sectionEntries.forEach(([key, value]) => {
          newSection[key] = value;
          if (key === editModal.selectedItem) newSection[safeName] = "";
        });
        newData.contract = { ...prev.contract, [sectionName]: newSection };
      }
      return newData;
    });
    setUiState(p => ({ ...p, saveStatus: "", isDirty: true }));
    closeEditModal();
  };
  const executeRemove = () => {
    setData(prev => {
      const newData = { ...prev };
      if (editModal.target === 'section') {
        const newContract = { ...prev.contract };
        editModal.multiSelect.forEach(sec => delete newContract[sec]);
        newData.contract = newContract;
      } else {
        const sectionName = editModal.section;
        const newSection = { ...prev.contract[sectionName] };
        editModal.multiSelect.forEach(field => delete newSection[field]);
        newData.contract = { ...prev.contract, [sectionName]: newSection };
      }
      return newData;
    });
    setUiState(p => ({ ...p, saveStatus: "", isDirty: true }));
    closeEditModal();
  };

  const saveChanges = async () => {
    if (!data.fileId) return;
    setUiState(p => ({ ...p, saveStatus: "Saving..." }));
    try {
      const res = await fetch(`${config.REACT_APP_API_URL}/redp/${data.fileId}`, { 
        method: "PUT", headers: { "Authorization": token, "Content-Type": "application/json" },
        body: JSON.stringify({ contract_data: data.contract, due_dates: data.dueDates }) 
      });
      if (!res.ok) throw new Error("Failed");
      setUiState(p => ({ ...p, saveStatus: "Changes Saved!", isDirty: false }));
      setTimeout(() => setUiState(p => ({ ...p, saveStatus: "" })), 3000);
      return true;
    } catch (err) {
      setUiState(p => ({ ...p, saveStatus: "Error saving changes." }));
      return false;
    }
  };

  const handleDownload = async (format) => {
    if (!data.fileId) return;
    const downloadName = getExportBaseName(formState.caseAlias, data.customization);
    const url = `${config.REACT_APP_API_URL}/AIModels/download/${data.fileId}?format=${format}&customizationName=${encodeURIComponent(downloadName)}`;
    try {
      const res = await fetch(url, { method: "GET", headers: { "Authorization": token } });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      let filename = `${downloadName}.${format}`;
      const disposition = res.headers.get('Content-Disposition');
      if (disposition && disposition.indexOf('attachment') !== -1) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
          if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, '');
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) { alert("Failed to download file."); }
  };

  const triggerBlobDownload = (blob, filename) => {
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  };

  const isDateFormat = (value) => {
    if (!value || typeof value !== "string") return false;
    const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim());
    if (!match) return false;
    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);
    const parsed = new Date(year, month - 1, day);
    return parsed.getFullYear() === year
      && parsed.getMonth() === month - 1
      && parsed.getDate() === day;
  };

  const generateIcsContent = () => {
    const { dates: mergedDates } = getMergedDates();
    const selectedDates = Object.entries(mergedDates).filter(([k]) => 
      calendarSelection.has(k)
    );

    if (selectedDates.length === 0) return null;

    let icsContent = 'BEGIN:VCALENDAR\r\nVERSION:2.0\r\nPRODID:-//RedP//Calendar//EN\r\n';

    // Use a counter to ensure unique UIDs even if Date.now() is the same
    let counter = 0;
    selectedDates.forEach(([key, value]) => {
      if (!value || typeof value !== 'string') return;
      const parts = value.split('/');
      if (parts.length === 3) {
        const m = parseInt(parts[0], 10);
        const d = parseInt(parts[1], 10);
        const y = parseInt(parts[2], 10);
        
        const pad = (n) => n.toString().padStart(2, '0');
        const dateStr = `${y}${pad(m)}${pad(d)}`;
        
        const caseAlias = formState.caseAlias.trim();
        const eventName = caseAlias ? `${caseAlias} - ${formatKey(key)}` : formatKey(key);
        const uid = `${Date.now()}-${counter++}-${key.replace(/\s/g, '-')}@redp.com`;

        icsContent += 'BEGIN:VEVENT\r\n';
        icsContent += `UID:${uid}\r\n`;
        icsContent += `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z\r\n`;
        icsContent += `DTSTART;VALUE=DATE:${dateStr}\r\n`;
        icsContent += `SUMMARY:${eventName}\r\n`;
        icsContent += 'END:VEVENT\r\n';
      }
    });

    icsContent += 'END:VCALENDAR\r\n';
    return icsContent;
  };

  const handleCalendarDownload = () => {
    if (calendarSelection.size === 0) {
      alert("Please select at least one date to add to the calendar.");
      return;
    }
    
    const icsContent = generateIcsContent();
    if (!icsContent) {
      alert("Failed to generate calendar file.");
      return;
    }

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    
    const downloadName = getExportBaseName(formState.caseAlias, data.customization, "Calendar");
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${downloadName}.ics`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    // Clean up the temporary URL
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const getSelectedExportFormatValues = () =>
    EXPORT_FORMAT_OPTIONS
      .map((format) => format.value)
      .filter((format) => selectedExportFormats.has(format));

  const toggleExportFormat = (format) => {
    setSelectedExportFormats((previous) => {
      const next = new Set(previous);
      if (next.has(format)) next.delete(format);
      else next.add(format);
      return next;
    });
  };

  const setAllExportFormats = (checked) => {
    setSelectedExportFormats(
      checked
        ? new Set(EXPORT_FORMAT_OPTIONS.map((format) => format.value))
        : new Set()
    );
  };

  const handleFormatDownload = async (format) => {
    if (format === "pdf") {
      handlePrintPDF();
      return;
    }
    if (format === "ics") {
      handleCalendarDownload();
      return;
    }
    await handleDownload(format);
  };

  const handleDownloadSelectedFormats = async () => {
    const formats = getSelectedExportFormatValues();
    if (formats.length === 0) {
      alert("Please select at least one format to download.");
      return;
    }
    if (formats.includes("ics") && calendarSelection.size === 0) {
      alert("Please select at least one date to include in the calendar file.");
      return;
    }

    setDownloadingExports(true);
    try {
      for (const format of formats) {
        await handleFormatDownload(format);
      }
    } finally {
      setDownloadingExports(false);
    }
  };

  const buildExportPayload = (formats) => {
    const payload = {
      file_id: data.fileId,
      customization_name: getExportBaseName(formState.caseAlias, data.customization, "Results"),
      formats,
    };

    if (formats.includes("pdf")) {
      payload.pdf_base64 = createResultsPdfDocument().doc.output("datauristring").split(",")[1];
    }

    if (formats.includes("ics")) {
      if (calendarSelection.size === 0) {
        throw new Error("Please select at least one date to include in the calendar file.");
      }
      const icsContent = generateIcsContent();
      if (!icsContent) {
        throw new Error("Failed to generate calendar file.");
      }
      payload.ics_content = icsContent;
    }

    return payload;
  };

  const handleDownloadZip = async () => {
    if (!data.fileId) return;
    const formats = getSelectedExportFormatValues();
    if (formats.length === 0) {
      alert("Please select at least one format to include in the ZIP.");
      return;
    }

    let payload;
    try {
      payload = buildExportPayload(formats);
    } catch (err) {
      alert(err.message);
      return;
    }

    setDownloadingZip(true);
    try {
      const res = await fetch(`${config.REACT_APP_API_URL}/redp/download/results/zip`, {
        method: "POST",
        headers: { "Authorization": token, "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.detail || "Failed to download ZIP");
      }

      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition');
      const matches = disposition ? /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition) : null;
      const filename = matches?.[1]?.replace(/['"]/g, '') || `${payload.customization_name}.zip`;
      triggerBlobDownload(blob, filename);
    } catch (err) {
      alert(`Error downloading ZIP: ${err.message}`);
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleEmailSelectedFormats = async () => {
    if (!data.fileId) return;
    const formats = getSelectedExportFormatValues();
    if (formats.length === 0) {
      alert("Please select at least one format to email.");
      return;
    }
    if (!exportEmail || !/\S+@\S+\.\S+/.test(exportEmail)) {
      alert("Please enter a valid email address.");
      return;
    }

    let payload;
    try {
      payload = {
        ...buildExportPayload(formats),
        email: exportEmail,
      };
    } catch (err) {
      alert(err.message);
      return;
    }

    setSendingExportEmail(true);
    try {
      const res = await fetch(`${config.REACT_APP_API_URL}/redp/send/results/email`, {
        method: "POST",
        headers: { "Authorization": token, "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.detail || "Failed to send email");
      }

      alert("Results email queued successfully! It should arrive shortly.");
    } catch (err) {
      alert(`Error sending results email: ${err.message}`);
    } finally {
      setSendingExportEmail(false);
    }
  };

  const toggleCalendarDate = (key) => {
      setCalendarSelection(prev => {
          const newSet = new Set(prev);
          if (newSet.has(key)) newSet.delete(key);
          else newSet.add(key);
          return newSet;
      });
  };

  // Merge all date-related data from dueDates and all contract sections
  // Returns { dates: {key: value}, sources: {key: source} }
  const getMergedDates = () => {
    const merged = {};
    const sources = {};
    
    // Add dates from data.dueDates
    if (data.dueDates) {
      Object.entries(data.dueDates).forEach(([key, value]) => {
        // Handle array values - take first element if it's a date
        const actualValue = Array.isArray(value) ? value[0] : value;
        if (actualValue && isDateFormat(actualValue)) {
          merged[key] = actualValue;
          sources[key] = 'dueDates';
        }
      });
    }
    
    // Scan all contract sections for date-formatted fields
    if (data.contract) {
      Object.entries(data.contract).forEach(([sectionName, fields]) => {
        Object.entries(fields).forEach(([key, value]) => {
          // Handle array values - take first element if it's a date
          const actualValue = Array.isArray(value) ? value[0] : value;
          // Only include if it's a valid date and not already added
          if (isDateFormat(actualValue) && actualValue && !merged.hasOwnProperty(key)) {
            merged[key] = actualValue;
            sources[key] = sectionName;
          }
        });
      });
    }
    
    return { dates: merged, sources };
  };

  const addToGoogleCalendar = (eventName, dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return;
    try {
        const parts = dateStr.split('/');
        if (parts.length !== 3) return;
        const m = parseInt(parts[0], 10);
        const d = parseInt(parts[1], 10);
        const y = parseInt(parts[2], 10);
        const startDate = new Date(y, m - 1, d);
        const endDate = new Date(y, m - 1, d);
        endDate.setDate(endDate.getDate() + 1); 

        const formatDate = (date) => date.toISOString().replace(/-|:|\.\d\d\d/g, "").split("T")[0];
        const start = formatDate(startDate);
        const end = formatDate(endDate);
        const formattedEventName = formatKey(eventName);
        const caseAlias = formState.caseAlias.trim();
        const title = encodeURIComponent(caseAlias ? `${caseAlias} - ${formattedEventName}` : formattedEventName);
        const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}`;
        window.open(url, '_blank');
    } catch (e) { console.error("Invalid date format", e); }
  };

  const removeFile = (idx) => setSelectedFiles(prev => prev.filter((_, i) => i !== idx));

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFiles.length) return setUiState(p => ({ ...p, fileError: "Please select a PDF file." }));
    setUiState(p => ({ ...p, loading: true, loaderText: "Processing...", error: "", fileError: "", dataSent: null, saveStatus: "", isDirty: false }));
    const timer = setTimeout(() => setUiState(p => ({ ...p, loaderText: "Analyzing document..." })), 15000);
    const formData = new FormData();
    formData.append("model_id", 4);
    selectedFiles.forEach(file => formData.append("files", file));
    if (formState.effectiveDate) formData.append("effective_date", formState.effectiveDate);
    try {
      const res = await fetch(`${config.REACT_APP_API_URL}/redp`, { method: "POST", body: formData, headers: { Authorization: token } });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || json.detail?.[0]?.msg || "Upload failed.");
      setData({
        contract: json.contract_data,
        dueDates: json.due_dates,
        fileId: json.file_id,
        customization: json.customization_name,
        fileName: json.original_filename,
        dateFieldTargets: json.date_field_targets || {},
      });
    } catch (err) { setUiState(p => ({ ...p, error: err.message })); } 
    finally { clearTimeout(timer); setUiState(p => ({ ...p, loading: false })); }
  };
  
  const validateProviderForm = () => {
    const errors = {};
    if (!providerState.providerId) errors.providerId = "Required";
    if (!providerState.appFileId) errors.appFileId = "Required";
    if (!providerState.email) errors.email = "Required";
    if (!providerState.password) errors.password = "Required";
    if (Object.keys(errors).length) {
      setUiState(p => ({ ...p, providerErrors: errors }));
      return false;
    }
    return true;
  };

  const buildProviderPayload = (overwriteFieldKeys) => ({
    file_id: data.fileId,
    provider_id: parseInt(providerState.providerId),
    app_file_id: providerState.appFileId,
    login_email: providerState.email,
    password: providerState.password,
    submit_documents: providerState.submitDocuments,
    submit_blank_fields: providerState.submitBlankFields,
    overwrite_field_keys: overwriteFieldKeys,
    contract_data: data.contract,
    due_dates: data.dueDates
  });

  const runAppFilesJob = async (operation, payload) => {
    const startResponse = await fetch(
      `${config.REACT_APP_API_URL}/redp/appfiles/jobs/${operation}`,
      {
        method: "POST",
        headers: { "Authorization": token, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );
    if (startResponse.status === 404) {
      const legacyPath = operation === "conflicts" ? "/redp/appfiles/conflicts" : "/redp/send";
      const legacyResponse = await fetch(`${config.REACT_APP_API_URL}${legacyPath}`, {
        method: "POST",
        headers: { "Authorization": token, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const legacyResult = await legacyResponse.json();
      if (!legacyResponse.ok) {
        throw new Error(legacyResult.error || legacyResult.detail || "AppFiles integration failed.");
      }
      return legacyResult;
    }
    const startedJob = await startResponse.json();
    if (!startResponse.ok) {
      throw new Error(startedJob.error || startedJob.detail || "Unable to join the AppFiles queue.");
    }

    appFilesJobRef.current = startedJob.job_id;
    setUiState(previous => ({
      ...previous,
      integrationProgress: startedJob.progress ?? 0,
      integrationStage: startedJob.message || "Waiting for AppFiles...",
      providerStatus: startedJob.message || "Waiting for AppFiles...",
      integrationQueuePosition: startedJob.queue_position ?? null,
      integrationCancelable: startedJob.can_cancel !== false,
      integrationCanceling: false,
    }));

    try {
      while (true) {
        const statusResponse = await fetch(
          `${config.REACT_APP_API_URL}/redp/appfiles/jobs/${startedJob.job_id}`,
          {
            headers: { "Authorization": token },
            cache: "no-store",
          }
        );
        const job = await statusResponse.json();
        if (!statusResponse.ok) {
          throw new Error(job.error || job.detail || "Unable to read AppFiles job progress.");
        }

        setUiState(previous => ({
          ...previous,
          integrationProgress: job.progress ?? 0,
          integrationStage: job.message || "Working in AppFiles...",
          providerStatus: job.message || "Working in AppFiles...",
          integrationQueuePosition: job.queue_position ?? null,
          integrationCancelable: Boolean(job.can_cancel),
          integrationCanceling: previous.integrationCanceling && job.status === "running",
        }));

        if (job.status === "completed") return job.result || {};
        if (job.status === "failed") {
          throw new Error(job.error || "AppFiles integration failed.");
        }
        if (job.status === "canceled") {
          const canceledError = new Error(job.message || "Your AppFiles submission was canceled.");
          canceledError.isCanceled = true;
          throw canceledError;
        }
        await wait(APPFILES_JOB_POLL_MS);
      }
    } finally {
      if (appFilesJobRef.current === startedJob.job_id) {
        appFilesJobRef.current = null;
      }
    }
  };

  const cancelCurrentAppFilesJob = async () => {
    const jobId = appFilesJobRef.current;
    if (!jobId || uiState.integrationCanceling) return;

    setUiState(previous => ({
      ...previous,
      integrationCanceling: true,
      integrationCancelable: false,
      integrationStage: "Requesting a safe cancellation...",
      providerStatus: "Requesting a safe cancellation...",
    }));

    try {
      const response = await fetch(
        `${config.REACT_APP_API_URL}/redp/appfiles/jobs/${jobId}/cancel`,
        {
          method: "POST",
          headers: { "Authorization": token },
        }
      );
      const job = await response.json();
      if (!response.ok) {
        throw new Error(job.error || job.detail || "Unable to cancel the AppFiles submission.");
      }
      setUiState(previous => ({
        ...previous,
        integrationProgress: job.progress ?? previous.integrationProgress,
        integrationStage: job.message || "Canceling your AppFiles submission...",
        providerStatus: job.message || "Canceling your AppFiles submission...",
        integrationQueuePosition: null,
        integrationCancelable: false,
        integrationCanceling: job.status === "running",
      }));
    } catch (error) {
      setUiState(previous => ({
        ...previous,
        integrationCanceling: false,
        integrationCancelable: true,
        integrationStage: `Unable to cancel: ${error.message}`,
        providerStatus: `Unable to cancel: ${error.message}`,
      }));
    }
  };

  const submitProviderPayload = async (payload) => {
    setUiState(p => ({
      ...p,
      sendingToProvider: true,
      providerStatus: APPFILES_CONFLICT_CHECK_MESSAGE,
      integrationProgress: 0,
      integrationStage: APPFILES_CONFLICT_CHECK_MESSAGE,
      integrationQueuePosition: null,
      integrationCancelable: false,
      integrationCanceling: false,
    }));
    try {
      if (parseInt(payload.provider_id) === 2) {
        await runAppFilesJob("send", payload);
      } else {
        const response = await fetch(`${config.REACT_APP_API_URL}/redp/send`, {
          method: "POST",
          headers: { "Authorization": token, "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const json = await response.json();
        if (!response.ok) throw new Error(json.error || "Failed");
      }
      setUiState(p => ({
        ...p,
        sendingToProvider: false,
        providerStatus: "Sent successfully!",
        dataSent: true,
        integrationProgress: null,
        integrationStage: "",
        integrationQueuePosition: null,
        integrationCancelable: false,
        integrationCanceling: false,
      }));
    } catch(err) {
      setUiState(p => ({
        ...p,
        sendingToProvider: false,
        providerStatus: err.isCanceled ? err.message : `Error: ${err.message}`,
        dataSent: err.isCanceled ? null : false,
        integrationProgress: null,
        integrationStage: "",
        integrationQueuePosition: null,
        integrationCancelable: false,
        integrationCanceling: false,
      }));
    }
  };

  const handleSendToProvider = async () => {
    if (!validateProviderForm()) return;
    const saved = await saveChanges();
    if (!saved) return;

    const payload = buildProviderPayload(isAppFilesProvider ? [] : undefined);
    if (!isAppFilesProvider) {
      await submitProviderPayload(payload);
      return;
    }

    setUiState(p => ({
      ...p,
      sendingToProvider: true,
      providerStatus: APPFILES_CONFLICT_CHECK_MESSAGE,
      integrationProgress: 0,
      integrationStage: APPFILES_CONFLICT_CHECK_MESSAGE,
      integrationQueuePosition: null,
      integrationCancelable: false,
      integrationCanceling: false,
    }));
    try {
      const json = await runAppFilesJob("conflicts", payload);
      if (json.sent) {
        setUiState(previous => ({
          ...previous,
          sendingToProvider: false,
          providerStatus: "Sent successfully!",
          dataSent: true,
          integrationProgress: null,
          integrationStage: "",
          integrationQueuePosition: null,
          integrationCancelable: false,
          integrationCanceling: false,
        }));
        return;
      }
      const leasedPayload = {
        ...payload,
        appfiles_session_id: json.appfiles_session_id,
      };

      if (json.conflicts?.length > 0) {
        setAppFilesConflictModal({
          isOpen: true,
          conflicts: json.conflicts,
          selected: new Set(json.conflicts.map(conflict => conflict.id)),
          payload: leasedPayload,
        });
        setUiState(p => ({
          ...p,
          sendingToProvider: false,
          providerStatus: `${json.conflicts.length} AppFiles conflict${json.conflicts.length === 1 ? "" : "s"} found.`,
          dataSent: null,
          integrationProgress: null,
          integrationStage: "",
          integrationQueuePosition: null,
          integrationCancelable: false,
          integrationCanceling: false,
        }));
        return;
      }

      await submitProviderPayload(leasedPayload);
    } catch(err) {
      setUiState(p => ({
        ...p,
        sendingToProvider: false,
        providerStatus: err.isCanceled ? err.message : `Error: ${err.message}`,
        dataSent: err.isCanceled ? null : false,
        integrationProgress: null,
        integrationStage: "",
        integrationQueuePosition: null,
        integrationCancelable: false,
        integrationCanceling: false,
      }));
    }
  };

  const toggleAppFilesConflict = (id) => {
    setAppFilesConflictModal(prev => {
      const selected = new Set(prev.selected);
      selected.has(id) ? selected.delete(id) : selected.add(id);
      return { ...prev, selected };
    });
  };

  const setAllAppFilesConflicts = (checked) => {
    setAppFilesConflictModal(prev => ({
      ...prev,
      selected: checked ? new Set(prev.conflicts.map(conflict => conflict.id)) : new Set(),
    }));
  };

  const closeAppFilesConflictModal = () => {
    const leasedPayload = appFilesConflictModal.payload;
    if (leasedPayload?.appfiles_session_id) {
      // Release the server-side Chrome lease immediately instead of waiting for
      // its TTL when the user cancels conflict review.
      fetch(`${config.REACT_APP_API_URL}/redp/appfiles/session/release`, {
        method: "POST",
        headers: { "Authorization": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          file_id: leasedPayload.file_id,
          provider_id: leasedPayload.provider_id,
          app_file_id: leasedPayload.app_file_id,
          appfiles_session_id: leasedPayload.appfiles_session_id,
        }),
        keepalive: true,
      }).catch(error => console.warn("Unable to release AppFiles session", error));
    }
    setAppFilesConflictModal({ isOpen: false, conflicts: [], selected: new Set(), payload: null });
    setUiState(p => ({ ...p, providerStatus: "Submission canceled; no AppFiles changes were sent.", dataSent: null }));
  };

  const submitSelectedAppFilesConflicts = async () => {
    const overwriteKeys = Array.from(appFilesConflictModal.selected);
    const payload = { ...appFilesConflictModal.payload, overwrite_field_keys: overwriteKeys };
    setAppFilesConflictModal({ isOpen: false, conflicts: [], selected: new Set(), payload: null });
    await submitProviderPayload(payload);
  };

  const formatKey = (key) => key.replace(/_/g, " ").replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

  const createResultsPdfDocument = () => {
    const { doc, startY } = createPdfDocument(formState.caseAlias);
    let y = startY;
    const addTable = (title, body) => {
      doc.setFontSize(14); doc.text(title, 14, y);
      autoTable(doc, { ...PDF_TABLE_OPTIONS, startY: y + 5, body });
      y = doc.lastAutoTable.finalY + 10;
    };
    if (data.contract) {
      Object.entries(data.contract).forEach(([sec, fields]) => addTable(sec, Object.entries(fields).map(([k, v]) => [formatKey(k), Array.isArray(v) ? v.join(", ") : (v ?? "-")])));
    }

    return { doc };
  };

  const handlePrintPDF = () => {
    const { doc } = createResultsPdfDocument();
    doc.save(`${getExportBaseName(formState.caseAlias, data.customization)}.pdf`);
  };

  const handleScrollClick = () => {
    if (resultsRef.current) {
        const offset = resultsRef.current.getBoundingClientRect().top + window.scrollY - 20; 
        window.scrollTo({ top: offset, behavior: "smooth" });
    } else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDocToggle = () => {
    if (selectedFiles.length === 0) return;
    if (selectedFiles.length === 1) {
      setDocViewerState(prev => prev.isOpen
        ? { ...prev, showMenu: !prev.showMenu }
        : { ...prev, isOpen: true, activeFile: selectedFiles[0], showMenu: false }
      );
    } else setDocViewerState(prev => ({ ...prev, showMenu: !prev.showMenu }));
  };

  const selectFileForDocView = (file) => setDocViewerState(prev => ({ ...prev, isOpen: true, activeFile: file, showMenu: false }));
  const closeDocPreview = () => setDocViewerState(prev => ({ ...prev, isOpen: false, activeFile: null, showMenu: false }));
  const activeFileIndex = selectedFiles.indexOf(docViewerState.activeFile);
  const showAdjacentFile = (offset) => {
    const nextFile = selectedFiles[activeFileIndex + offset];
    if (nextFile) selectFileForDocView(nextFile);
  };
  const toggleSwap = () => setDocViewerState(prev => ({ ...prev, swapLayout: !prev.swapLayout }));

  useEffect(() => { if (uiState.loading) loaderRef.current?.scrollIntoView({ behavior: "smooth" }); }, [uiState.loading]);

  const canScrollToResults = showScrollTop && (data.fileId || data.contract);
  const isSplitMode = docViewerState.isOpen && docViewerState.activeFile;
  const isSwapped = docViewerState.swapLayout;
  const isAppFilesProvider = String(providerState.providerId) === "2";

  return (
    <div className={`main-page-container ${isSplitMode ? 'split-view-active' : ''}`}>
      <div className="nav-bar">
          <Link to="/downloads" className="tools-downloads-link" title="Tools & Downloads">
             <DownloadCircleIcon />
             <span>Tools & Downloads</span>
          </Link>
          {user.user_type === "admin" && (
            <Link to="/admin" className="logout-btn admin-nav-link" title="Open system administration">
              Admin dashboard
            </Link>
          )}
          <button onClick={logout} className="logout-btn" title="Log Out">
             <LogoutIcon />
             <span className="logout-text">Log Out</span>
          </button>
      </div>

      <div className={`split-wrapper ${isSwapped ? 'swapped' : ''}`}>
          {/* DATA PANE */}
          <div className="data-pane-wrapper">
              <div className="main-card animate-fade-in">
                <div className="main-header">
                  <img src={logo} alt="Logo" className="main-logo" />
                  <h1>Document Processor</h1>
                </div>

                <form onSubmit={handleUpload} className="main-form">
                  <div className="form-section">
                    <div className="input-group">
                      <label className="input-label">Upload PDF(s) <span className="required">*</span></label>
                      <div 
                        className={`file-upload-wrapper ${isDragging ? "drag-active" : ""}`}
                        onDragOver={handleDragOver} onDragEnter={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
                      >
                        <input 
                            type="file" accept="application/pdf" id="file" className="file-input-hidden" multiple
                            onChange={(e) => { if (e.target.files) setSelectedFiles(prev => [...prev, ...Array.from(e.target.files)]); }} 
                            onClick={e => e.target.value = null} 
                        />
                        <label htmlFor="file" className="file-upload-button">{isDragging ? "Drop files here..." : "Select Document(s)"}</label>
                      </div>
                      {selectedFiles.length > 0 && (
                        <div className="file-cards-container animate-fade-in">
                          {selectedFiles.map((file, idx) => (
                            <div key={`${file.name}-${idx}`} className="file-card-item">
                              <div className="file-card-icon">📄</div>
                              <div className="file-card-info">
                                <span className="file-card-name" title={file.name}>{file.name}</span>
                                <span className="file-card-size">{(file.size / 1024).toFixed(0)} KB</span>
                              </div>
                              <button type="button" className="file-card-remove" onClick={() => removeFile(idx)}>×</button>
                            </div>
                          ))}
                        </div>
                      )}
                      {uiState.fileError && <div className="error-message-inline">{uiState.fileError}</div>}
                    </div>
                    <div className="input-group">
                      <label className="input-label" htmlFor="case-alias">Case Alias <span className="optional-text"> (Optional)</span></label>
                      <input
                        id="case-alias"
                        type="text"
                        className="text-input"
                        placeholder="Enter a case alias"
                        maxLength={120}
                        value={formState.caseAlias}
                        onChange={e => updateForm("caseAlias", e.target.value)}
                      />
                    </div>
                    <div className="input-group">
                      <label className="input-label">Effective Date <span className="optional-text"> (Optional)</span></label>
                      <input type="date" className="text-input" value={formState.effectiveDate} onChange={e => updateForm("effectiveDate", e.target.value)} />
                    </div>
                  </div>

                  <div className="acknowledgement-section">
                    <label className="checkbox-container">
                      <input type="checkbox" checked={acknowledgement} onChange={(e) => setAcknowledgement(e.target.checked)} />
                      <span className="checkbox-text">I acknowledge that I have read and agree to the Terms of Service and Privacy Policy. I understand that this tool uses generative AI which may produce inaccurate results, and that all results can be fully modified before exporting or use.</span>
                    </label>
                  </div>

                  <div className="button-group">
                    <button type="submit" className="primary-button" disabled={uiState.loading || !acknowledgement || selectedFiles.length === 0}>
                      {uiState.loading ? "Processing..." : "Submit and Process"}
                    </button>
                  </div>
                </form>

                <div className="customization-section">
                  <button className="customize-link-btn" onClick={() => setShowCustomization(true)}>Customize Output Configuration</button>
                </div>

                {uiState.loading && <div ref={loaderRef} className="loader-container"><Loader text={uiState.loaderText} /></div>}
                {uiState.error && <div className="error-banner">{uiState.error}</div>}

                {!uiState.loading && (data.fileId || data.dueDates || data.contract) && (
                  <div className="results-container animate-fade-in">
                     <div ref={resultsRef} className="divider"></div>
                     <div className="provider-actions-pane">
                        <div className="pane-header">
                          <h3>Integrations</h3>
                          <button 
                            className="secondary-button compact-btn" 
                            style={{width: 'auto', height: '32px', padding: '0 10px'}}
                            onClick={() => setIntegrationsCollapsed(!integrationsCollapsed)}
                          >
                            {integrationsCollapsed ? "Expand" : "Collapse"}
                          </button>
                        </div>
                        {!integrationsCollapsed && (
                          <div className="pane-content">
                              <p className="pane-desc">Review your data below, then send to your preferred provider.</p>
                              <div className="provider-row">
                                  <div className="input-group">
                                      <label className="input-label">Provider</label>
                                      <select className="select-input" value={providerState.providerId} onChange={e => updateProviderForm("providerId", e.target.value)}>
                                          <option value="">Select Provider...</option>
                                          {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                      </select>
                                  </div>
                                  {providerState.providerId && (
                                      <>
                                          <div className="input-group">
                                              <label className="input-label">File ID</label>
                                              <input className="text-input" type="text" placeholder="ID from URL" value={providerState.appFileId} onChange={e => updateProviderForm("appFileId", e.target.value)} />
                                          </div>
                                          <div className="input-group">
                                              <label className="input-label">Email</label>
                                              <input className="text-input" type="email" value={providerState.email} onChange={e => updateProviderForm("email", e.target.value)} />
                                          </div>
                                          <div className="input-group">
                                              <label className="input-label">Password</label>
                                              <div className="password-wrapper">
                                                <input className="text-input password-input" type={uiState.showPassword ? "text" : "password"} value={providerState.password} onChange={e => updateProviderForm("password", e.target.value)} />
                                                <button type="button" className="password-toggle-btn" onClick={() => setUiState(p => ({ ...p, showPassword: !p.showPassword }))}>
                                                  {uiState.showPassword ? <EyeOffIcon /> : <EyeIcon />}
                                                </button>
                                              </div>
                                          </div>
                                          {isAppFilesProvider && (
                                            <div className="appfiles-options">
                                              <label className="provider-option-toggle">
                                                <input
                                                  type="checkbox"
                                                  checked={providerState.submitDocuments}
                                                  onChange={e => updateProviderForm("submitDocuments", e.target.checked)}
                                                />
                                                <span>
                                                  <strong>Submit documents</strong>
                                                  <small>Upload the original PDFs to AppFiles</small>
                                                </span>
                                              </label>
                                              <label className="provider-option-toggle">
                                                <input
                                                  type="checkbox"
                                                  checked={providerState.submitBlankFields}
                                                  onChange={e => updateProviderForm("submitBlankFields", e.target.checked)}
                                                />
                                                <span>
                                                  <strong>Submit blank fields</strong>
                                                  <small>Create or update AppFiles fields even when values are blank</small>
                                                </span>
                                              </label>
                                            </div>
                                          )}
                                          <div className="input-group send-btn-group">
                                              <label className="input-label">&nbsp;</label>
                                              <button className="primary-button small-btn" onClick={handleSendToProvider} disabled={uiState.sendingToProvider}>
                                                {uiState.sendingToProvider
                                                  ? (uiState.integrationQueuePosition ? "Queued..." : "Sending...")
                                                  : (isAppFilesProvider ? "Send to AppFiles" : "Send Data")}
                                              </button>
                                          </div>
                                      </>
                                  )}
                              </div>
                              {uiState.integrationProgress !== null && (
                                <div className="integration-progress" aria-live="polite">
                                  <div className="integration-progress-label">
                                    <span>{uiState.integrationStage || "Working in AppFiles..."}</span>
                                    <strong>{Math.round(uiState.integrationProgress)}%</strong>
                                  </div>
                                  <div
                                    className="integration-progress-track"
                                    role="progressbar"
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                    aria-valuenow={Math.round(uiState.integrationProgress)}
                                  >
                                    <div
                                      className="integration-progress-fill"
                                      style={{ width: `${Math.max(0, Math.min(100, uiState.integrationProgress))}%` }}
                                    />
                                  </div>
                                  <div className="integration-progress-actions">
                                    {uiState.integrationQueuePosition && (
                                      <span className="integration-queue-position">
                                        Position {uiState.integrationQueuePosition} in line
                                      </span>
                                    )}
                                    {(uiState.integrationCancelable || uiState.integrationCanceling) && (
                                      <button
                                        type="button"
                                        className="integration-cancel-button"
                                        onClick={cancelCurrentAppFilesJob}
                                        disabled={uiState.integrationCanceling}
                                      >
                                        {uiState.integrationCanceling ? "Canceling..." : "Cancel submission"}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                              {uiState.providerStatus && uiState.integrationProgress === null && (
                                <div className={`status-message ${uiState.dataSent ? 'success' : 'info'}`}>
                                  {uiState.providerStatus}
                                </div>
                              )}
                          </div>
                        )}
                     </div>

                     {/* Results Export Section */}
                     {data.fileId && (
                        <div className="provider-actions-pane results-export-pane" style={{marginTop: '1.5rem'}}>
                          <div className="pane-header">
                            <h3>Export Results</h3>
                          </div>
                          <div className="pane-content">
                            {uiState.isDirty && <p className="unsaved-warning">You have unsaved changes!</p>}
                            <p className="pane-desc">Select one or more formats, then download or email them. Use the arrow icon to download a single format.</p>
                            {selectedExportFormats.has("ics") && (
                              <p className="export-helper-note">ICS includes the dates checked in the extracted data below.</p>
                            )}
                            <div className="export-select-row">
                              <label className="export-select-all">
                                <input
                                  type="checkbox"
                                  checked={selectedExportFormats.size === EXPORT_FORMAT_OPTIONS.length}
                                  onChange={(event) => setAllExportFormats(event.target.checked)}
                                />
                                <span>Select all formats</span>
                              </label>
                              <span className="export-count">{selectedExportFormats.size} selected</span>
                            </div>
                            <div className="export-format-list">
                              {EXPORT_FORMAT_OPTIONS.map((format) => {
                                const isSelected = selectedExportFormats.has(format.value);
                                return (
                                  <div
                                    key={format.value}
                                    className={`export-format-row ${isSelected ? "selected" : ""}`}
                                    role="checkbox"
                                    aria-checked={isSelected}
                                    tabIndex={0}
                                    onClick={() => toggleExportFormat(format.value)}
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        toggleExportFormat(format.value);
                                      }
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      aria-label={`Select ${format.label}`}
                                      onClick={(event) => event.stopPropagation()}
                                      onChange={() => toggleExportFormat(format.value)}
                                    />
                                    <div className="export-format-copy">
                                      <span>{format.label}</span>
                                      <small>{format.description}</small>
                                    </div>
                                    <button
                                      type="button"
                                      className="export-format-download"
                                      aria-label={`Download ${format.label}`}
                                      title={`Download ${format.label}`}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleFormatDownload(format.value);
                                      }}
                                    >
                                      <DownloadCircleIcon />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="export-action-row">
                              <div className="export-email-field">
                                <label className="input-label" style={{marginBottom: '5px'}}>Email Address</label>
                                <input
                                  type="email"
                                  placeholder="Enter recipient email..."
                                  className="text-input"
                                  value={exportEmail}
                                  onChange={(e) => setExportEmail(e.target.value)}
                                />
                              </div>
                              <button
                                className="secondary-button small-btn"
                                onClick={handleDownloadSelectedFormats}
                                disabled={downloadingExports || downloadingZip || sendingExportEmail || selectedExportFormats.size === 0}
                              >
                                {downloadingExports ? "Downloading..." : "Download"}
                              </button>
                              <button
                                className="secondary-button small-btn"
                                onClick={handleDownloadZip}
                                disabled={downloadingExports || downloadingZip || sendingExportEmail || selectedExportFormats.size === 0}
                              >
                                {downloadingZip ? "Zipping..." : "Download ZIP"}
                              </button>
                              <button
                                className="primary-button small-btn"
                                onClick={handleEmailSelectedFormats}
                                disabled={sendingExportEmail || downloadingExports || downloadingZip || selectedExportFormats.size === 0}
                              >
                                {sendingExportEmail ? "Sending..." : "Email"}
                              </button>
                            </div>
                          </div>
                        </div>
                     )}

                     {uiState.isDirty && (
                        <div className="results-header-actions">
                            <button className="download-btn secondary-download save-btn" onClick={saveChanges}>Save Changes</button>
                            {uiState.saveStatus && <span className={`save-status-text ${uiState.saveStatus.includes("Error") ? "error" : "success"}`}>{uiState.saveStatus}</span>}
                        </div>
                     )}

                    <div ref={printRef} className="data-preview-area">
                      {/* --- Results Header --- */}
                      {(data.dueDates || data.contract) && (
                          <div className="section-header-group" style={{marginBottom: '2rem', borderBottom: '2px solid var(--border-color)', paddingBottom: '0.5rem'}}>
                             <h3 style={{fontSize: '1.3rem'}}>Extracted Data</h3>
                             {data.contract && (
                                <div className="section-tools">
                                    <button className="icon-btn" onClick={() => openEditModal('add', null, 'section')} title="Add Section">+</button>
                                    <button className="icon-btn" onClick={() => openEditModal('remove', null, 'section')} title="Remove Section">−</button>
                                </div>
                             )}
                          </div>
                      )}

                      {/* --- Contract Data Sections --- */}
                      {data.contract && Object.entries(data.contract).map(([sec, fields]) => (
                        <div key={sec} className="data-section">
                          <div className="section-header-group">
                            <h3>{sec}</h3>
                            <div className="section-tools">
                                <button className="icon-btn" onClick={() => openEditModal('add', sec)} title="Add Field">+</button>
                                <button className="icon-btn" onClick={() => openEditModal('remove', sec)} title="Remove Field">−</button>
                            </div>
                          </div>
                          <div className="table-wrapper">
                            <table className="main-data-table">
                              <tbody>
                                {Object.entries(fields).map(([k, v]) => {
                                  const isDate = isDateFormat(v);
                                  return (
                                  <tr key={k}>
                                    <td className="key-col">
                                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between'}}>
                                            <textarea className="data-table-input key-input" defaultValue={formatKey(k)} onBlur={(e) => handleKeyChange(sec, k, e.target.value)} onKeyDown={handleKeyDown} rows={1} style={{flex: 1}} />
                                            {canSplitField(k, v) && (
                                                <button className="icon-btn" style={{width: '24px', height: '24px', fontSize: '10px'}} onClick={() => handleSplitField(sec, k)} title="Split">
                                                    <SplitIcon />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="val-col">
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        {isDate && v && (
                                          <input 
                                            type="checkbox" 
                                            checked={calendarSelection.has(k)} 
                                            onChange={() => toggleCalendarDate(k)}
                                            style={{ transform: 'scale(1.2)', cursor: 'pointer', flexShrink: 0 }}
                                            title="Include in calendar export"
                                          />
                                        )}
                                        <textarea
                                          className="data-table-input"
                                          value={Array.isArray(v) ? v.join(", ") : (v ?? "")}
                                          onChange={(e) => handleInputChange(sec, k, e.target.value, e)}
                                          onFocus={() => handleEffectiveDateFocus(sec, k, v)}
                                          onBlur={(e) => handleDateFieldBlur(sec, k, e.target.value)}
                                          onKeyDown={handleKeyDown}
                                          rows={1}
                                          style={{flex: 1}}
                                        />
                                        {isDate && v && (
                                          <button 
                                            className="icon-btn" 
                                            onClick={() => addToGoogleCalendar(k, v)}
                                            title="Add to Google Calendar"
                                            style={{ padding: '4px', width: '28px', height: '28px', color: '#5f6368', flexShrink: 0 }}
                                          >
                                            <CalendarPlusIcon />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )})}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
          </div>
          
          {/* DOC VIEWER PANE */}
          {isSplitMode && (
              <div className="doc-pane-wrapper animate-fade-in">
                  <div className="doc-pane-header">
                      {selectedFiles.length > 1 && <button className="doc-nav-btn" onClick={() => showAdjacentFile(-1)} disabled={activeFileIndex <= 0} title="Previous document" aria-label="Previous document"><ChevronIcon direction="left" /></button>}
                      <span className="doc-pane-filename" title={docViewerState.activeFile?.name}>{docViewerState.activeFile?.name}</span>
                      {selectedFiles.length > 1 && <button className="doc-nav-btn" onClick={() => showAdjacentFile(1)} disabled={activeFileIndex < 0 || activeFileIndex >= selectedFiles.length - 1} title="Next document" aria-label="Next document"><ChevronIcon direction="right" /></button>}
                      <button className="close-btn-small" onClick={closeDocPreview} title="Close preview" aria-label="Close preview">×</button>
                  </div>
                  <div className="doc-pane-body">
                      <iframe src={previewUrl} title="Document Preview" className="doc-iframe" />
                  </div>
              </div>
          )}
      </div>

      {showCustomization && (
        <div className="provider-overlay animate-fade-in" onClick={() => setShowCustomization(false)}>
          <div className="provider-window" onClick={(e) => e.stopPropagation()}>
            <button className="provider-close-btn" onClick={() => setShowCustomization(false)}>×</button>
            <div className="provider-body-scroll"><Customizations userId={user.id} /></div>
          </div>
        </div>
      )}

      {/* Effective Date Recalculation Preview */}
      {recalculateModal.isOpen && (
        <div className="field-modal-overlay animate-fade-in" onClick={closeRecalculateModal}>
          <div className="field-modal-content recalculate-modal" onClick={event => event.stopPropagation()}>
            <div className="field-modal-header">
              <h4>Recalculate dependent dates?</h4>
              <button className="close-btn-small" onClick={closeRecalculateModal}>×</button>
            </div>
            <div className="field-modal-body">
              <p className="modal-instruction">
                The Effective Date changed to {recalculateModal.effectiveDate}. Select the deadlines to update.
              </p>
              <div className="recalculate-list">
                {recalculateModal.changes.map(change => (
                  <label className="recalculate-row" key={change.key}>
                    <input
                      type="checkbox"
                      checked={recalculateModal.selected.has(change.key)}
                      onChange={() => toggleRecalculatedDate(change.key)}
                    />
                    <span className="recalculate-label">{formatKey(change.key)}</span>
                    <span className="recalculate-date old-date">{change.old_date || "—"}</span>
                    <span className="recalculate-arrow" aria-hidden="true">→</span>
                    <span className="recalculate-date new-date">{change.new_date}</span>
                  </label>
                ))}
              </div>
              <p className="recalculate-note">Unselected deadlines will keep their current values.</p>
              <div className="modal-actions">
                <button className="secondary-download" onClick={closeRecalculateModal}>Keep existing dates</button>
                <button className="primary-button small-btn" onClick={applyRecalculatedDates}>Apply selected</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AppFiles Conflict Review */}
      {appFilesConflictModal.isOpen && (
        <div className="field-modal-overlay animate-fade-in" onClick={closeAppFilesConflictModal}>
          <div className="field-modal-content appfiles-conflict-modal" onClick={event => event.stopPropagation()}>
            <div className="field-modal-header">
              <h4>AppFiles conflicts</h4>
              <button className="close-btn-small" onClick={closeAppFilesConflictModal}>×</button>
            </div>
            <div className="field-modal-body">
              <p className="modal-instruction">
                These AppFiles fields already have values. Select the ones REDP should overwrite.
              </p>
              <div className="conflict-toolbar">
                <button className="secondary-download" onClick={() => setAllAppFilesConflicts(true)}>Select all</button>
                <button className="secondary-download" onClick={() => setAllAppFilesConflicts(false)}>Select none</button>
                <span>{appFilesConflictModal.selected.size} of {appFilesConflictModal.conflicts.length} selected</span>
              </div>
              <div className="appfiles-conflict-list">
                {appFilesConflictModal.conflicts.map(conflict => (
                  <label className="appfiles-conflict-row" key={conflict.id}>
                    <input
                      type="checkbox"
                      checked={appFilesConflictModal.selected.has(conflict.id)}
                      onChange={() => toggleAppFilesConflict(conflict.id)}
                    />
                    <div className="conflict-copy">
                      <div className="conflict-title">
                        <span>{conflict.section}</span>
                        <strong>{conflict.field}</strong>
                      </div>
                      <div className="conflict-values">
                        <div>
                          <small>AppFiles</small>
                          <p>{conflict.current_value}</p>
                        </div>
                        <div>
                          <small>REDP</small>
                          <p>{conflict.new_value || "(blank)"}</p>
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <p className="recalculate-note">Unselected conflicts will be skipped. New fields and non-conflicting fields will still be submitted.</p>
              <div className="modal-actions">
                <button className="secondary-download" onClick={closeAppFilesConflictModal}>Cancel</button>
                <button className="primary-button small-btn" onClick={submitSelectedAppFilesConflicts}>
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Field/Section Modal */}
      {editModal.isOpen && (
        <div className="field-modal-overlay animate-fade-in" onClick={closeEditModal}>
            <div className="field-modal-content" onClick={e => e.stopPropagation()}>
                <div className="field-modal-header">
                    <h4>{editModal.type === 'add' ? 'Add New ' : 'Remove '} {editModal.target === 'section' ? 'Section' : 'Field'}</h4>
                    <button className="close-btn-small" onClick={closeEditModal}>×</button>
                </div>
                <div className="field-modal-body">
                    {editModal.step === 'select' && (
                        <>
                            <p className="modal-instruction">{editModal.type === 'add' ? `Insert AFTER:` : `Select to remove:`}</p>
                            <div className="fields-list-scroll">
                                {editModal.type === 'add' && <div className="field-list-item special-option" onClick={() => handleItemSelect('__TOP__')}>-- Add to Top --</div>}
                                {(editModal.target === 'section' ? Object.keys(data.contract || {}) : Object.keys(data.contract[editModal.section] || {})).map(itemKey => (
                                      <div key={itemKey} className={`field-list-item ${editModal.type === 'remove' && editModal.multiSelect.has(itemKey) ? 'selected' : ''}`} onClick={() => handleItemSelect(itemKey)}>
                                          <span className="field-name">{editModal.target === 'section' ? itemKey : formatKey(itemKey)}</span>
                                      </div>
                                ))}
                            </div>
                            {editModal.type === 'remove' && <div className="modal-actions"><button className="secondary-download" onClick={closeEditModal}>Cancel</button><button className="primary-button small-btn danger-btn" onClick={proceedToDeleteConfirm} disabled={editModal.multiSelect.size === 0}>Delete</button></div>}
                        </>
                    )}
                    {editModal.step === 'input' && (
                        <div className="input-step-container">
                            <label className="input-label">Name</label>
                            <input className="text-input" autoFocus value={newItemName} onChange={e => setNewItemName(e.target.value)} />
                            <div className="modal-actions"><button className="secondary-download" onClick={() => setEditModal(p => ({...p, step: 'select'}))}>Back</button><button className="primary-button small-btn" onClick={executeAdd}>Add</button></div>
                        </div>
                    )}
                    {editModal.step === 'confirm' && (
                        <div className="confirm-step-container"><p>Are you sure?</p><div className="modal-actions"><button className="secondary-download" onClick={() => setEditModal(p => ({...p, step: 'select'}))}>Back</button><button className="primary-button small-btn danger-btn" onClick={executeRemove}>Delete</button></div></div>
                    )}
                </div>
            </div>
        </div>
      )}

      {/* --- CONTACT MODAL --- */}
      {showContactModal && (
        <div className="provider-overlay animate-fade-in" onClick={() => setShowContactModal(false)}>
          <div className="provider-window" onClick={(e) => e.stopPropagation()} style={{ height: 'auto', maxHeight: '90vh' }}>
            <button className="provider-close-btn" onClick={() => setShowContactModal(false)}>×</button>
            <div className="provider-body-scroll">
              <ContactForm />
            </div>
          </div>
        </div>
      )}

      {/* --- FLOATING CONTROLS & HELP MENU --- */}
      <div className="help-widget-container">
          {helpMenuOpen && (
              <div className="help-menu animate-fade-in">
                  <div className="menu-item" onClick={() => { setShowContactModal(true); setHelpMenuOpen(false); }}>Contact Support</div>
                  <Link to="/terms-and-conditions" className="menu-item" style={{textDecoration: 'none'}}>Terms of Service</Link>
                  <Link to="/privacy-policy" className="menu-item" style={{textDecoration: 'none'}}>Privacy Policy</Link>
              </div>
          )}
          <button className={`float-btn help-btn ${helpMenuOpen ? 'active' : ''}`} onClick={() => setHelpMenuOpen(!helpMenuOpen)} title="Help & Support">
              <HelpIcon />
          </button>
      </div>

      <div className={`floating-controls-container ${(canScrollToResults || selectedFiles.length > 0) ? 'visible' : ''}`}>
        {canScrollToResults && <button className="float-btn" onClick={handleScrollClick}><ArrowUpIcon /></button>}
        {selectedFiles.length > 0 && (
            <div className="preview-btn-wrapper">
                <button className={`float-btn preview-trigger ${isSplitMode ? 'active' : ''}`} onClick={handleDocToggle}><DocumentIcon /></button>
                {isSplitMode && <button className="float-btn swap-trigger" onClick={toggleSwap}><SwapIcon /></button>}
                {docViewerState.showMenu && (
                    <div className="preview-file-menu animate-fade-in">
                        {selectedFiles.map((file, idx) => <div key={idx} className="menu-item" title={file.name} onClick={() => selectFileForDocView(file)}>{file.name}</div>)}
                        {isSplitMode && <div className="menu-item close-preview-item" onClick={closeDocPreview}>Close preview</div>}
                    </div>
                )}
            </div>
        )}
      </div>

    </div>
  );
};
export default Main;
